// When a notice that reached this screen becomes an OS notification.
//
// Promise: a notice is shown only when
// - it is for this screen: it names no desktops and this is a browser (the
//   offline edition tells every open screen; its desktop app shows notices
//   from main, so its windows do not), or it names this desktop app's
//   installation (cloud tells only the desktops the person registered;
//   browsers get web push),
// - the notification's page is not already open in the window in front,
// - this device shows OS notifications (the device setting; when never set,
//   on inside the desktop app or once the browser allowed notifications),
// - and it carries a title and a path to open.
import { defineDeviceSetting } from "../device/device-setting";
import type { OsNotice } from "../device/os-notices";

/** null: never set on this device (the default applies). */
export const osNoticesSetting = defineDeviceSetting<boolean | null>({
  key: "trameria-os-notices",
  parse: (raw) => (raw === "1" ? true : raw === "0" ? false : null),
  serialize: (v) => (v === null ? "" : v ? "1" : "0"),
});

export function osNoticesOn(
  setting: boolean | null,
  device: {
    inDesktopApp: boolean;
    permission: NotificationPermission | null;
  },
): boolean {
  if (setting !== null) return setting;
  return device.inDesktopApp || device.permission === "granted";
}

export interface NoticeShowInput {
  payload: unknown;
  /** This desktop app's installation id, null in a browser. */
  installationId: string | null;
  inFront: boolean;
  /** The page shown now; read when the notice arrives, not when subscribing. */
  pathname: string;
  enabled: boolean;
}

export function noticeToShow(input: NoticeShowInput): OsNotice | null {
  if (!input.enabled) return null;
  const p = (input.payload ?? {}) as Record<string, unknown>;
  if (p.desktops !== undefined) {
    const ids = Array.isArray(p.desktops) ? p.desktops : [];
    if (input.installationId === null || !ids.includes(input.installationId))
      return null;
  } else if (input.installationId !== null) return null;
  if (typeof p.title !== "string" || p.title === "") return null;
  if (typeof p.url !== "string" || !p.url.startsWith("/")) return null;
  if (input.inFront && showsNoticePage(input.pathname, p.url)) return null;
  return {
    title: p.title,
    body: typeof p.body === "string" ? p.body : "",
    url: p.url,
    tag: typeof p.tag === "string" ? p.tag : "",
    badge: typeof p.badge === "number" ? p.badge : 0,
    ...noticeActions(p.actions),
  };
}

/** A notice's buttons (paths in the app only); none when it has none. */
function noticeActions(raw: unknown): Pick<OsNotice, "actions"> {
  if (!Array.isArray(raw)) return {};
  const actions = raw.flatMap((a) => {
    const { id, label, url } = (a ?? {}) as Record<string, unknown>;
    return typeof id === "string" &&
      typeof label === "string" &&
      typeof url === "string" &&
      url.startsWith("/")
      ? [{ id, label, url }]
      : [];
  });
  return actions.length ? { actions } : {};
}

/** A side view of the same page still leaves its notification in sight. */
export function showsNoticePage(pathname: string, target: string): boolean {
  try {
    const page = decodeURIComponent(
      new URL(target, "https://app.invalid").pathname,
    ).replace(/\/$/, "");
    const current = decodeURIComponent(pathname).replace(/\/$/, "");
    return current === page || current.startsWith(`${page}/`);
  } catch {
    return false;
  }
}
